import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import { getChartThemeColors, observeThemeChange } from "utils/chartTheme";

// Renders an inline chart for a Kit "mini_report" or "composed" block from the
// JSON config in data-kit-chart-config-value. Uses Chart.js (so it survives
// Turbo-Stream appends, unlike chartkick's inline <script>) and the shared
// chartTheme for theme-aware chrome. Re-renders on dark/light toggle.
//
// Series colours are the design system's --chart-1..3, validated for both
// themes (see _theme_tokens.scss); the hex list is only the fallback for a page
// that somehow lacks the tokens.
const FALLBACK_SERIES_COLORS = ["#0A7EA4", "#C4802B", "#6B5CA5"];

function seriesColors() {
  const style = getComputedStyle(document.documentElement);
  return FALLBACK_SERIES_COLORS.map(
    (fallback, i) =>
      style.getPropertyValue(`--chart-${i + 1}`).trim() || fallback
  );
}

// A horizontal bar chart's labels get at most half the chart's width; past
// that Chart.js clips the start of the label. Shorten it to fit instead,
// measured in the tick font at the chart's current width.
const TICK_FONT_SIZE = 12;

function fitLabel(value) {
  let label = String(this.getLabelForValue(value));
  const room = this.chart.width / 2 - 14;
  const ctx = this.ctx;
  ctx.save();
  ctx.font = `${TICK_FONT_SIZE}px ${Chart.defaults.font.family}`;
  if (ctx.measureText(label).width > room) {
    while (label.length > 1 && ctx.measureText(`${label}…`).width > room) {
      label = label.slice(0, -1);
    }
    label = `${label.trimEnd()}…`;
  }
  ctx.restore();
  return label;
}

// 1234567 -> "1.2M": value ticks read like the stat cards beside them.
function compactNumber(value) {
  const n = Number(value);
  if (Math.abs(n) >= 1e6) return `${+(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${+(n / 1e3).toFixed(1)}K`;
  return `${n}`;
}

export default class extends Controller {
  static values = { config: Object };
  static targets = ["canvas"];

  connect() {
    this.draw();
    this.stopObserving = observeThemeChange(() => this.draw());
  }

  disconnect() {
    if (this.stopObserving) this.stopObserving();
    if (this.chart) this.chart.destroy();
  }

  draw() {
    if (!this.hasCanvasTarget) return;
    if (this.chart) this.chart.destroy();

    const cfg = this.configValue || {};
    const type = (cfg.chart_type || "line").toLowerCase();
    const labels = cfg.labels || [];
    const series = cfg.series || [];
    const theme = getChartThemeColors();
    const isPie = type === "pie" || type === "doughnut";
    this.colors = seriesColors();

    this.chart = new Chart(this.canvasTarget, {
      type: this.chartJsType(type),
      data: isPie
        ? this.pieData(labels, series)
        : this.seriesData(type, labels, series),
      options: this.options(type, theme, series.length, isPie),
    });
  }

  // Private

  chartJsType(type) {
    if (type === "column") return "bar";
    if (type === "area") return "line";
    if (type === "doughnut") return "doughnut";
    if (type === "pie") return "pie";
    return type; // line | bar
  }

  seriesData(type, labels, series) {
    return {
      labels,
      datasets: series.map((s, i) => {
        const base = this.colors[i % this.colors.length];
        return {
          label: s.name || "",
          data: s.data || [],
          backgroundColor:
            type === "line"
              ? "transparent"
              : type === "area"
              ? this.rgba(base, 0.15)
              : base,
          borderColor: base,
          borderWidth: type === "line" || type === "area" ? 2 : 0,
          borderRadius: type === "column" || type === "bar" ? 4 : 0,
          maxBarThickness: 48,
          tension: 0.35,
          fill: type === "area",
          pointRadius: 0,
        };
      }),
    };
  }

  pieData(labels, series) {
    const data = (series[0] && series[0].data) || [];
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: labels.map(
            (_, i) => this.colors[i % this.colors.length]
          ),
          borderWidth: 0,
        },
      ],
    };
  }

  options(type, theme, seriesCount, isPie) {
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: type === "bar" ? "y" : "x",
      plugins: {
        legend: {
          display: isPie || seriesCount > 1,
          labels: { color: theme.legendColor, boxWidth: 12 },
        },
        tooltip: {
          backgroundColor: theme.tooltipBackgroundColor,
          titleColor: theme.tooltipTitleColor,
          bodyColor: theme.tooltipBodyColor,
          borderColor: theme.tooltipBorderColor,
          borderWidth: 1,
        },
      },
    };

    if (!isPie) {
      // A horizontal bar chart swaps the axes: its values run along x. Chart.js
      // gives a vertical axis at most half the chart's width, so in a panel this
      // narrow the labels stay small or a campaign name gets clipped.
      const valueAxis = type === "bar" ? "x" : "y";
      const categoryAxis = valueAxis === "x" ? "y" : "x";
      const font = { size: TICK_FONT_SIZE };
      opts.scales = {
        [categoryAxis]: {
          grid: { display: false },
          ticks: {
            color: theme.tickColor,
            font,
            ...(categoryAxis === "y" ? { callback: fitLabel } : {}),
          },
        },
        [valueAxis]: {
          beginAtZero: true,
          grid: { color: theme.gridColor },
          ticks: { color: theme.tickColor, font, callback: compactNumber },
        },
      };
    }

    return opts;
  }

  rgba(hex, alpha) {
    const n = parseInt(hex.replace("#", ""), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
}
